import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  Operation,
  createHttpLink,
  from
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { ErrorHandler, onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import {
  SimpleApolloOperation,
  extractSimpleApolloOperation,
  operationMatchesSimpleApolloOperation,
  useApolloOperationErrorToast
} from "@pathwright/utils/src/graphql/apolloOperation"
import cache from "@pathwright/web-new/src/api/cache"
import PropTypes from "prop-types"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { getEnv, getIsLocalDev, getPathwrightDomain } from "../lib/env"
import { getIsPWAdminUtilEnabled } from "../pw-admin/useIsPWAdminUtilEnabled"
import ApolloClientStoreBridge from "./ApolloClientStoreBridge"
import { usePathwrightContext } from "./PathwrightContext"

export const getGraphQLEndpoint = () => {
  // returns the proper graphql server endpoint based on the current env

  let isLocalDev = getIsLocalDev()

  let domain = ``
  const pathwrightDomain = getPathwrightDomain()

  if (process.env.REACT_APP_GQL) {
    domain = process.env.REACT_APP_GQL
    isLocalDev = false
  } else if (pathwrightDomain) {
    // one of pathwright's domains
    domain = `gql.${pathwrightDomain}.com`
  } else {
    // custom domain
    domain = "gql.pathwright.com"
  }

  // if (isLocalDev) {
  //   // ROBERT TESTING WHILE TRAVELING
  //   return `https://gql.pathwrightstaging.com/`
  // }

  const PORT = process.env.SERVER_PORT || 8000

  const base = isLocalDev
    ? `http://${window.location.hostname}:${PORT}/`
    : `https://${domain}/`

  return base
}

type Client = ApolloClient<any> & { _consistentReadClient: ApolloClient<any> }

type ApolloOperationHandler = (operation: Operation) => void

// This is a singleton pattern
let _client = null as unknown as Client

type GetApolloClientOptions = {
  schoolId?: number
  operationHandler?: ApolloOperationHandler
  errorHandler?: ErrorHandler
  reset?: boolean
}

export const getApolloClient = (options: GetApolloClientOptions = {}) => {
  const { schoolId, operationHandler, errorHandler, reset = false } = options

  // If in "test" env, attempt to use the window.testClient
  // TODO: should this rather be handled via jest mocks?
  if (getEnv("test") && window.testClient && _client !== window.testClient) {
    _client = window.testClient
  }

  if (_client && !reset) return _client
  if (_client) {
    _client.clearStore()
    _client._consistentReadClient.clearStore()
  }

  // Optionally handle the operation, for instance, logging it.
  const operationLink = new ApolloLink((operation, forward) => {
    operationHandler?.(operation)
    return forward(operation)
  })

  const httpLink = createHttpLink({
    uri: (operation) => {
      let uri = `${getGraphQLEndpoint()}graphql?school_id=${schoolId}`

      // Supply an operationName as a convenience for identifying the operation
      // via the endpoint, which, for instance, is all that is included in
      // Sentry breadcrumbs. So, this can help with debugging.
      if (operation.operationName) {
        uri += `&operationName=${operation.operationName}`
      }

      return uri
    }
  })

  const schoolLink = setContext((_, { headers }) => {
    let nextHeaders = { ...headers }
    nextHeaders.school_id = schoolId
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = localStorage.auth_token

    let nextHeaders = { ...headers }

    if (token) {
      nextHeaders.authorization = `Bearer ${token}`
    }
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  const consistentReadMiddlewareLink = setContext((_, { headers }) => {
    let nextHeaders = { ...headers }
    nextHeaders.ensure_consistent_read = true
    // return the headers to the context so httpLink can read them
    return {
      headers: nextHeaders
    }
  })

  // https://www.apollographql.com/docs/link/links/retry
  // Default is 5 attempts
  const retryLink = new RetryLink()

  const errorLink = onError((options) => {
    const { graphQLErrors, operation, forward } = options

    errorHandler?.(options)

    // Removed stored auth_token when invalid.
    if (
      graphQLErrors &&
      graphQLErrors.find(
        ({ message }) =>
          message ===
          `Invalid token. An error occurred while verifying the token: The provided token was revoked.`
      ) &&
      localStorage.getItem("auth_token")
    ) {
      localStorage.removeItem("auth_token")
      return forward(operation)
    }
  })

  const clientOptions = {
    link: from([
      operationLink,
      retryLink,
      schoolLink,
      authLink,
      errorLink,
      httpLink
    ]),
    cache,
    name: "web",
    version: "1.0"
  }

  _client = new ApolloClient(clientOptions) as Client

  // Optional client used for ensuring GQL server reads data from the main database
  // as opposed to the read replica database.
  // _client._consistentReadClient = new ApolloClient({
  //   ...clientOptions,
  //   link: from([retryLink, consistentReadMiddlewareLink, httpLink])
  // })
  _client._consistentReadClient = _client

  return _client
}

export const usePathwrightClient = () => {
  const { school } = usePathwrightContext()
  const client = getApolloClient({ schoolId: school.id })
  return client
}

type PathwrightClientProps = {
  schoolId: number
  children: JSX.Element | (({ client }: { client: Client }) => JSX.Element)
  operationHandler?: ApolloOperationHandler
}

type PathwrightClientValue = {
  operations: SimpleApolloOperation[]
}

export const PathwrightClientContext = createContext<PathwrightClientValue>(
  {} as PathwrightClientValue
)

export const usePathwrightClientContext = () =>
  useContext(PathwrightClientContext)

export const PathwrightClient = ({
  schoolId,
  children
}: PathwrightClientProps) => {
  const [client, setClient] = useState<Client | null>(null)
  const [operations, setOperations] = useState<SimpleApolloOperation[]>([])
  const enableOperationHandler = useMemo(getIsPWAdminUtilEnabled, [])

  const operationErrorToast = useApolloOperationErrorToast()

  const errorHandler: ErrorHandler = (options) => {
    // Runs in local dev for any user.
    operationErrorToast(options)

    const { operation, graphQLErrors } = options
    // Only record error if PW Admin Util is enabled.
    if (graphQLErrors && getIsPWAdminUtilEnabled()) {
      // Defer state updates to avoid updating this component during another
      // component's render phase (e.g., when Apollo kicks off an operation
      // synchronously during useQuery render in a child component).
      // Using a macrotask ensures the update occurs after the current render commit.
      setTimeout(() => {
        // Updating the matching operation to indicate it has an error.
        setOperations((prevOperations) =>
          prevOperations.map((op) =>
            operationMatchesSimpleApolloOperation(operation, op) && !op.hasError
              ? {
                  ...op,
                  hasError: true
                }
              : op
          )
        )
      }, 0)
    }
  }

  // Conditionally store operations only if the PW Admin Util is enabled.
  const MAX_OPERATIONS = 500
  const operationHandler: ApolloOperationHandler = (operation) => {
    if (enableOperationHandler) {
      // Defer state updates to avoid updating this component during another
      // component's render phase (e.g., when Apollo kicks off an operation
      // synchronously during useQuery render in a child component).
      // Using a macrotask ensures the update occurs after the current render commit.
      setTimeout(() => {
        setOperations((prevOperations) => {
          // Storing in order of most recent first.
          const newOperations = [
            extractSimpleApolloOperation(operation),
            ...prevOperations
          ]
          return newOperations.slice(0, MAX_OPERATIONS) // Keep only the last MAX_OPERATIONS
        })
      }, 0)
    }
  }

  useEffect(() => {
    // reseting current client, if there is one
    const client = getApolloClient({
      schoolId,
      operationHandler,
      errorHandler,
      reset: true
    })
    setClient(client)
    // NOTE: setting globally as temporary solution to
    // syncing backbone/mobx stores and apollo client.
    // See apollo client syncer.
    window.apolloClientStoreBridge = new ApolloClientStoreBridge(client)
  }, [schoolId])

  return client ? (
    <PathwrightClientContext.Provider value={{ operations }}>
      <ApolloProvider client={client}>
        {typeof children === "function" ? children({ client }) : children}
      </ApolloProvider>
    </PathwrightClientContext.Provider>
  ) : null
}

PathwrightClient.displayName = "PathwrightClient"

PathwrightClient.propTypes = {
  schoolId: PropTypes.number.isRequired,
  onAuthChange: PropTypes.func
}

export default function withPathwrightClient(ComposedComponent: any) {
  const ComposedPathwrightClient = ({
    schoolId,
    ...props
  }: {
    schoolId: number
  }) => (
    <PathwrightClient schoolId={schoolId}>
      <ComposedComponent {...props} />
    </PathwrightClient>
  )

  return ComposedPathwrightClient
}
